import type { FC, ReactNode } from 'react'
import type { MessageTemplateKey } from '@/types'
import type { CheckoutTarget } from '@/components/billing/checkout-sheet'
import type { WhatsAppTarget } from '@/components/messages/whatsapp-sheet'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { StaffPickerSheet } from '@/components/jobs/staff-picker-sheet'
import { StatusSheet } from '@/components/jobs/status-sheet'
import { CheckoutSheet } from '@/components/billing/checkout-sheet'
import { WhatsAppSheet } from '@/components/messages/whatsapp-sheet'

type JobActionsContextValue = {
    openAssign: (jobId: string) => void
    openStatus: (jobId: string) => void
    openComplete: (jobId: string) => void
    openPayment: (invoiceId: string) => void
    openWhatsApp: (input: { jobId?: string | null, invoiceId?: string | null, template: MessageTemplateKey }) => void
    highlightJob: (jobId: string) => void
    highlightedJobId: string | null
}

const JobActionsContext = createContext<JobActionsContextValue | null>(null)

/*
 * The sheets for working on a vehicle live here once, so the dashboard,
 * the vehicle list, the vehicle page and billing all open exactly the same
 * flows.
 */
export const JobActionsProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [assignJobId, setAssignJobId] = useState<string | null>(null)
    const [assignOpen, setAssignOpen] = useState(false)
    const [statusJobId, setStatusJobId] = useState<string | null>(null)
    const [statusOpen, setStatusOpen] = useState(false)
    const [checkoutTarget, setCheckoutTarget] = useState<CheckoutTarget | null>(null)
    const [checkoutOpen, setCheckoutOpen] = useState(false)
    const [whatsAppTarget, setWhatsAppTarget] = useState<WhatsAppTarget | null>(null)
    const [whatsAppOpen, setWhatsAppOpen] = useState(false)
    const [highlightedJobId, setHighlightedJobId] = useState<string | null>(null)
    const highlightTimer = useRef<number | null>(null)

    const openAssign = useCallback((jobId: string) => {
        setAssignJobId(jobId)
        setAssignOpen(true)
    }, [])

    const openStatus = useCallback((jobId: string) => {
        setStatusJobId(jobId)
        setStatusOpen(true)
    }, [])

    const openComplete = useCallback((jobId: string) => {
        setCheckoutTarget({ kind: 'job', jobId })
        setCheckoutOpen(true)
    }, [])

    const openPayment = useCallback((invoiceId: string) => {
        setCheckoutTarget({ kind: 'invoice', invoiceId })
        setCheckoutOpen(true)
    }, [])

    const openWhatsApp = useCallback((input: { jobId?: string | null, invoiceId?: string | null, template: MessageTemplateKey }) => {
        setWhatsAppTarget({ jobId: input.jobId ?? null, invoiceId: input.invoiceId ?? null, template: input.template })
        setWhatsAppOpen(true)
    }, [])

    const highlightJob = useCallback((jobId: string) => {
        setHighlightedJobId(jobId)

        if (highlightTimer.current) window.clearTimeout(highlightTimer.current)
        highlightTimer.current = window.setTimeout(() => setHighlightedJobId(null), 3200)
    }, [])

    useEffect(() => () => {
        if (highlightTimer.current) window.clearTimeout(highlightTimer.current)
    }, [])

    const value = useMemo<JobActionsContextValue>(() => ({
        openAssign,
        openStatus,
        openComplete,
        openPayment,
        openWhatsApp,
        highlightJob,
        highlightedJobId
    }), [openAssign, openStatus, openComplete, openPayment, openWhatsApp, highlightJob, highlightedJobId])

    return (
        <JobActionsContext.Provider value={value}>
            {children}

            <StaffPickerSheet jobId={assignJobId} open={assignOpen} onClose={() => setAssignOpen(false)} />

            <StatusSheet
                jobId={statusJobId}
                open={statusOpen}
                onClose={() => setStatusOpen(false)}
                onAssign={openAssign}
                onComplete={openComplete}
            />

            <CheckoutSheet
                target={checkoutTarget}
                open={checkoutOpen}
                onClose={() => setCheckoutOpen(false)}
                onWhatsApp={openWhatsApp}
            />

            <WhatsAppSheet target={whatsAppTarget} open={whatsAppOpen} onClose={() => setWhatsAppOpen(false)} />
        </JobActionsContext.Provider>
    )
}

export const useJobActions = (): JobActionsContextValue => {
    const context = useContext(JobActionsContext)
    if (!context) throw new Error('useJobActions must be used inside JobActionsProvider')
    return context
}
